export const COVERAGE_DIMENSION_CODES = [
  'COM',
  'TAS',
  'IMP',
  'RUL',
  'KNO',
  'QUE',
  'RIS',
  'ACC',
  'DEP',
  'OBS',
  'UX',
  'IMPX',
] as const;

export type CoverageDimensionCode = (typeof COVERAGE_DIMENSION_CODES)[number];

export interface CoverageDimensionDefinition {
  code: CoverageDimensionCode;
  name: string;
  intent: string;
  outcome: string;
}

export interface CoverageDimensionScore {
  code: CoverageDimensionCode;
  score: number;
  reasoning?: string;
}

export const COVERAGE_DIMENSIONS: CoverageDimensionDefinition[] = [
  {
    code: 'COM',
    name: 'Context',
    intent: 'Why does this exist?',
    outcome: 'Clear business/user motivation and surrounding context.',
  },
  {
    code: 'TAS',
    name: 'Task / Use Case',
    intent: 'What should happen?',
    outcome: 'Concrete workflows, user actions, edge journeys.',
  },
  {
    code: 'IMP',
    name: 'Implementation',
    intent: 'How could this realistically be built?',
    outcome: 'Technical direction, APIs, dependencies, architecture impact.',
  },
  {
    code: 'RUL',
    name: 'Rules & Constraints',
    intent: 'What must or must not happen?',
    outcome: 'Validation rules, permissions, compliance, limitations, performance constraints.',
  },
  {
    code: 'KNO',
    name: 'Knowledge Coverage',
    intent: 'Do we actually understand the domain/problem?',
    outcome: 'Sufficient linked discussions, decisions, prior knowledge, references.',
  },
  {
    code: 'QUE',
    name: 'Questions Resolved',
    intent: 'What uncertainty still exists?',
    outcome: 'Open questions minimized or explicitly accepted.',
  },
  {
    code: 'RIS',
    name: 'Risks',
    intent: 'What could fail or become expensive later?',
    outcome: 'Identified technical, UX, operational, security, or business risks.',
  },
  {
    code: 'ACC',
    name: 'Acceptance Criteria',
    intent: 'How do we know it is done correctly?',
    outcome: 'Testable success conditions.',
  },
  {
    code: 'DEP',
    name: 'Dependencies',
    intent: 'What does this rely on?',
    outcome: 'External systems, teams, infra, sequencing identified.',
  },
  {
    code: 'OBS',
    name: 'Observability',
    intent: 'How will we monitor reality after deployment?',
    outcome: 'Logging, metrics, alerts, analytics, Sentry/PostHog mapping.',
  },
  {
    code: 'UX',
    name: 'UX / Product Alignment',
    intent: 'Does this match intended experience?',
    outcome: 'Design references, interaction expectations, copy, states.',
  },
  {
    code: 'IMPX',
    name: 'Change Impact',
    intent: 'What existing things are affected?',
    outcome: 'Migration risks, backward compatibility, affected flows/components.',
  },
];

export function buildCoverageDimensionPromptBlock(): string {
  return COVERAGE_DIMENSIONS.map(
    d => `- ${d.code} (${d.name}): ${d.intent} Outcome: ${d.outcome}`,
  ).join('\n');
}

export function normalizeCoverageDimensionScores(
  scores: CoverageDimensionScore[],
): CoverageDimensionScore[] {
  const byCode = new Map(scores.map(s => [s.code, s]));
  return COVERAGE_DIMENSION_CODES.map(code => {
    const existing = byCode.get(code);
    return {
      code,
      score: existing?.score ?? 0,
      reasoning: existing?.reasoning,
    };
  });
}
